/**
 * Arthaus "size on the wall" (Phase 10, HD-PD-SCALE / HD-SYS-SCALE)
 * docs/storefront-redesign/requirements/4_product_detail/HIGH_DESIGN_TRD.md
 *
 * Markup: snippets/hd-scale-scene.liquid (server-rendered at the current size),
 * data in [data-hd-pdp-data] (sections/product-hero.liquid).
 *
 * On product-config.js's `pdp:variant-change` the artwork tweens to its new size
 * against a 210 cm sofa, 160 cm bed or 170 cm person (FLIP on the art group,
 * --ease-spring), and the caption / SVG label restate the size in plain-words
 * fractions, never percentages. The frame is a nominal 2 cm, never stated.
 *
 * Flag: html[data-hd-scale]. Re-initialises after a frame-switch swap (`pdp:swapped`);
 * listeners on the persistent hero element are bound once.
 */
window.ArthausHDScale = window.ArthausHDScale || (function () {
  'use strict';

  var FRAME_CM = 2;
  var REFS = {
    sofa:   { size: 210, axis: 'w', noun: 'a 3-seat sofa', label: 'shown above a 210 centimetre three-seat sofa' },
    bed:    { size: 160, axis: 'w', noun: 'a double bed', label: 'shown above a double bed 160 centimetres wide' },
    person: { size: 170, axis: 'h', noun: 'a 170 cm person', label: 'shown beside a person 170 centimetres tall' }
  };
  var TONES = { black: '#262523', white: '#efece6', oak: '#c4a275', walnut: '#6a4a31' };
  var UNIT_KEY = 'hd-scale-unit';
  var state = null;

  function on() { return document.documentElement.hasAttribute('data-hd-scale'); }
  function reduced() { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; }

  // "50cm x 70cm (20'' x 28'')" → { w: 50, h: 70, inW: '20', inH: '28' }
  function parseSize(value) {
    var s = String(value || '');
    var cm = s.match(/(\d+(?:\.\d+)?)\s*cm\s*[x×]\s*(\d+(?:\.\d+)?)\s*cm/i);
    if (!cm) return null;
    var inch = s.match(/(\d+(?:\.\d+)?)\s*(?:''|"|″|in)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    return { w: parseFloat(cm[1]), h: parseFloat(cm[2]), inW: inch && inch[1], inH: inch && inch[2] };
  }

  // Nearest plain-words fraction (unit-tested thresholds mirrored in hd-scale-scene.liquid).
  function fraction(r) {
    if (r < 0.146) return 'about ⅛';
    if (r < 0.183) return 'about ⅙';
    if (r < 0.225) return 'about ⅕';
    if (r < 0.292) return 'about ¼';
    if (r < 0.417) return 'about ⅓';
    if (r < 0.583) return 'about ½';
    if (r < 0.708) return 'about ⅔';
    if (r < 0.875) return 'about ¾';
    return null;                                  // → "about as wide/tall as"
  }
  function comparison(ow, oh, refKey) {
    var ref = REFS[refKey];
    var r = (ref.axis === 'w' ? ow : oh) / ref.size;
    var f = fraction(r);
    var dim = ref.axis === 'w' ? 'width' : 'height';
    if (!f) return (ref.axis === 'w' ? 'about as wide as ' : 'about as tall as ') + ref.noun;
    return f + ' the ' + dim + ' of ' + ref.noun;
  }

  function readUnit() { try { return localStorage.getItem(UNIT_KEY) === 'in' ? 'in' : 'cm'; } catch (e) { return 'cm'; } }
  function saveUnit(u) { try { localStorage.setItem(UNIT_KEY, u); } catch (e) {} }

  function dimsText() {
    var s = state.size;
    if (state.unit === 'in' && s.inW && s.inH) return s.inW + '″ × ' + s.inH + '″';
    return s.w + ' × ' + s.h + ' cm';
  }

  function geometry() {
    var f = state.framed ? FRAME_CM : 0;
    var ow = state.size.w + 2 * f, oh = state.size.h + 2 * f;
    return { f: f, ow: ow, oh: oh, x: 200 - ow / 2, y: 115 - oh / 2 };
  }

  function measurements(g) {
    var layer = state.root.querySelector('[data-hd-scale-dimensions]');
    if (!layer) return;
    layer.replaceChildren();
    function node(tag, attrs, label) {
      var item = document.createElementNS('http://www.w3.org/2000/svg', tag);
      Object.keys(attrs).forEach(function (key) { item.setAttribute(key, attrs[key]); });
      if (label) item.textContent = label;
      layer.append(item);
    }
    function line(x1, y1, x2, y2) { node('line', { x1:x1, y1:y1, x2:x2, y2:y2 }); }
    function dimension(x1, y1, x2, y2, label, vertical) {
      line(x1,y1,x2,y2); line(x1-2,y1+2,x1+2,y1-2); line(x2-2,y2+2,x2+2,y2-2);
      node('text', { x:vertical?x1+5:(x1+x2)/2, y:vertical?(y1+y2)/2:y1-5, 'text-anchor':vertical?'start':'middle' }, label);
    }
    var x = g.x+g.f, y = g.y+g.f, w = state.size.w, h = state.size.h;
    var width = state.unit==='in' && state.size.inW ? state.size.inW+' in' : w+' cm';
    var height = state.unit==='in' && state.size.inH ? state.size.inH+' in' : h+' cm';
    line(x,y-3,x,y-20); line(x+w,y-3,x+w,y-20); dimension(x,y-16,x+w,y-16,width,false);
    line(x+w+3,y,x+w+20,y); line(x+w+3,y+h,x+w+20,y+h); dimension(x+w+16,y,x+w+16,y+h,height,true);
    if (state.ref === 'person') { line(354,89,389,89); line(354,259,389,259); dimension(382,89,382,259,state.unit==='in'?'67 in':'170 cm',true); }
    else { var left=state.ref==='bed'?120:95, right=state.ref==='bed'?280:305;
      line(left,262,left,283); line(right,262,right,283); dimension(left,279,right,279,state.unit==='in'?(REFS[state.ref].size/2.54).toFixed(1)+' in':REFS[state.ref].size+' cm',false); }
  }

  function render(animate) {
    var g = geometry();
    var el = state.els;
    var prev = el.outer ? { w: parseFloat(el.outer.getAttribute('width')), h: parseFloat(el.outer.getAttribute('height')) } : null;

    function rect(node, x, y, w, h) {
      if (!node) return;
      node.setAttribute('x', x); node.setAttribute('y', y);
      node.setAttribute('width', w); node.setAttribute('height', h);
    }
    rect(el.shadow, g.x + 1.2, g.y + 1.8, g.ow, g.oh);
    rect(el.outer, g.x, g.y, g.ow, g.oh);
    rect(el.sheet, g.x + g.f, g.y + g.f, state.size.w, state.size.h);
    rect(el.image, g.x + g.f, g.y + g.f, state.size.w, state.size.h);
    if (el.outer && state.framed) el.outer.setAttribute('fill', TONES[state.profile] || TONES.oak);

    // FLIP: start from the old size and settle into the new one.
    if (animate && prev && el.art && el.art.animate && !reduced() && (prev.w !== g.ow || prev.h !== g.oh)) {
      var easing = getComputedStyle(document.documentElement).getPropertyValue('--ease-spring').trim() || 'cubic-bezier(0.2, 0.7, 0.1, 1)';
      var frames = [{ transform: 'scale(' + (prev.w / g.ow) + ', ' + (prev.h / g.oh) + ')' }, { transform: 'none' }];
      try { el.art.animate(frames, { duration: 520, easing: easing }); }
      catch (e) { el.art.animate(frames, { duration: 520, easing: 'cubic-bezier(0.2, 0.7, 0.1, 1)' }); }
    }

    var dims = dimsText();
    measurements(g);
    if (el.caption) {
      el.caption.textContent = dims + ' · ' + comparison(g.ow, g.oh, state.ref);
      el.caption.dataset.cm = state.size.w + ' × ' + state.size.h + ' cm';
      if (state.size.inW && state.size.inH) el.caption.dataset.in = state.size.inW + '″ × ' + state.size.inH + '″';
    }
    if (el.svg) el.svg.setAttribute('aria-label', (state.unit === 'in' && state.size.inW ? state.size.inW + ' by ' + state.size.inH + ' inch' : state.size.w + ' by ' + state.size.h + ' centimetre') + ' artwork ' + REFS[state.ref].label);
  }

  function setRef(key) {
    state.ref = key;
    state.root.querySelectorAll('[data-hd-scale-ref]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-hd-scale-ref') === key));
    });
    state.root.querySelectorAll('[data-hd-scale-ref-shape]').forEach(function (s) {
      s.style.display = s.getAttribute('data-hd-scale-ref-shape') === key ? '' : 'none';
    });
    render(false);
  }

  function track(action) {
    if (!state || !state.root._ahExperienceEmit) return;
    state.root._ahExperienceEmit({ action: action === 'view' ? 'size_change' : action === 'reference' ? 'reference_change' : action,
      properties: { size: state.size.w + 'x' + state.size.h, unit: state.unit, reference: state.ref } });
  }

  function onVariantChange(e) {
    if (!state || !on() || !e.detail || !e.detail.variant) return;
    var d = state.data, opts = e.detail.variant.options || [];
    var size = d.sizePos > 0 ? parseSize(opts[d.sizePos - 1]) : null;
    if (!size) return;
    if (d.landscape && size.w < size.h) size = { w: size.h, h: size.w, inW: size.inH, inH: size.inW };
    var changed = size.w !== state.size.w || size.h !== state.size.h;
    state.size = size;
    if (d.framePos > 0) {
      var p = d.profiles && d.profiles[opts[d.framePos - 1]];
      if (p) state.profile = p.key;
    }
    render(e.detail.isUser && changed);
    if (e.detail.isUser && changed) track('view');
  }

  function bindOnce(hero) {
    if (hero._hdScaleBound) return;
    hero._hdScaleBound = true;
    hero.addEventListener('pdp:variant-change', onVariantChange);
    hero.addEventListener('click', function (e) {
      if (!state) return;
      var ref = e.target.closest('[data-hd-scale-ref]');
      if (ref) { setRef(ref.getAttribute('data-hd-scale-ref')); track('reference'); return; }
      if (e.target.closest('[data-hd-scale-unit]')) {
        state.unit = state.unit === 'in' ? 'cm' : 'in';
        saveUnit(state.unit);
        render(false);
        track('unit_change');
        return;
      }
      var toggle = e.target.closest('[data-hd-scale-toggle]');
      if (toggle) {
        var open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));
        state.els.panel.classList.toggle('max-lg:hidden', !open);
        if (open) track('view');
      }
    });
  }

  function init() {
    state = null;
    if (!on()) return;
    var hero = document.querySelector('[data-product-hero]');
    var root = hero && hero.querySelector('[data-hd-scale-scene]');
    var dataEl = hero && hero.querySelector('[data-hd-pdp-data]');
    if (!root || !dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var sheet = root.querySelector('[data-hd-scale-sheet]');
    if (!sheet) return;
    var captionEl = root.querySelector('[data-hd-scale-caption]');
    var cmText = (captionEl && captionEl.getAttribute('data-cm')) || '';
    var inText = (captionEl && captionEl.getAttribute('data-in')) || '';
    var inMatch = inText.match(/(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/);

    state = {
      hero: hero,
      root: root,
      data: data,
      framed: root.getAttribute('data-framed') === 'true',
      size: {
        w: parseFloat(sheet.getAttribute('width')),
        h: parseFloat(sheet.getAttribute('height')),
        inW: inMatch && inMatch[1], inH: inMatch && inMatch[2]
      },
      profile: null,
      ref: 'sofa',
      unit: readUnit(),
      els: {
        svg: root.querySelector('[data-hd-scale-svg]'),
        art: root.querySelector('[data-hd-scale-art]'),
        shadow: root.querySelector('[data-hd-scale-shadow]'),
        outer: root.querySelector('[data-hd-scale-outer]'),
        sheet: sheet,
        image: root.querySelector('[data-hd-scale-image]'),
        caption: captionEl,
        panel: root.querySelector('[data-hd-scale-panel]')
      }
    };
    if (!cmText) state.unit = 'cm';
    var outerFill = state.els.outer && state.els.outer.getAttribute('fill');
    Object.keys(TONES).forEach(function (k) { if (TONES[k] === outerFill) state.profile = k; });
    bindOnce(hero);
    render(false);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('pdp:swapped', init);

  return { init: init, parseSize: parseSize, fraction: fraction, comparison: comparison };
})();
